import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getAllRegionSettings,
    createRegionSettings,
    updateRegionSettings,
    deleteRegionSettings,
    getRegionSettingsById,
    RegionSettingsParams,
    CreateRegionSettingsData
} from "../Actions/RegionSettingsAction";

// Interface for region settings data
export interface RegionSettings {
    id: string;
    name: string;
    code: string;
    status?: string;
    supportedLanguages: Array<{ id: string; name?: string }>;
    serviceAreas: Array<{ id: string; name?: string }>;
    quoteCurrency: { id: string; name?: string };
    defaultCurrency: { id: string; name?: string };
    defaultLanguage: { id: string; name?: string };
    supportPhone: string;
    supportEmail: string;
    businessWeekendEndHour: string;
    businessWeekendStartHour: string;
    businessWeekdayEndHour: string;
    businessWeekdayStartHour: string;
    commissionEmergencyServiceHoliday: number;
    commissionEmergencyServiceWeekend: number;
    commissionEmergencyServiceNight: number;
    commissionEmergencyServiceDay: number;
    commissionRegularService: number;
    quoteAmount: number;
    createdAt?: string;
    updatedAt?: string;
}

// Interface for the state
interface RegionSettingsState {
    regionSettings: RegionSettings[];
    selectedRegionSettings: RegionSettings | null;
    loading: boolean;
    error: string | null;
    totalCount: number;
    hasNextPage: boolean;
    currentPage: number;
    currentLimit: number;
}

// Initial state
const initialState: RegionSettingsState = {
    regionSettings: [],
    selectedRegionSettings: null,
    loading: false,
    error: null,
    totalCount: 0,
    hasNextPage: false,
    currentPage: 1,
    currentLimit: 10,
};

// Create the slice
const regionSettingsSlice = createSlice({
    name: "regionSettings",
    initialState,
    reducers: {
        clearSelectedRegionSettings: (state) => {
            state.selectedRegionSettings = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },
        setCurrentLimit: (state, action: PayloadAction<number>) => {
            state.currentLimit = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Get All Region Settings
        builder
            .addCase(getAllRegionSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllRegionSettings.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                if (action.payload) {
                    state.regionSettings = action.payload.data || [];
                    state.totalCount = action.payload.totalCount || 0;
                    state.hasNextPage = action.payload.hasNextPage || false;
                }
            })
            .addCase(getAllRegionSettings.rejected, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.error = action.payload || "Failed to fetch region settings";
            });

        // Create Region Settings
        builder
            .addCase(createRegionSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createRegionSettings.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                if (action.payload) {
                    // Add the new region settings to the list
                    state.regionSettings.unshift(action.payload);
                    state.totalCount += 1;
                }
            })
            .addCase(createRegionSettings.rejected, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.error = action.payload || "Failed to create region settings";
            });

        // Update Region Settings
        builder
            .addCase(updateRegionSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateRegionSettings.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                if (action.payload) {
                    // Update the region settings in the list
                    const index = state.regionSettings.findIndex(
                        (item) => item.id === action.payload.id
                    );
                    if (index !== -1) {
                        state.regionSettings[index] = action.payload;
                    }
                    // Update selected region settings if it's the same one
                    if (state.selectedRegionSettings?.id === action.payload.id) {
                        state.selectedRegionSettings = action.payload;
                    }
                }
            })
            .addCase(updateRegionSettings.rejected, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.error = action.payload || "Failed to update region settings";
            });

        // Delete Region Settings
        builder
            .addCase(deleteRegionSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteRegionSettings.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                // Remove the deleted region settings from the list
                state.regionSettings = state.regionSettings.filter(
                    (item) => item.id !== action.payload?.id
                );
                state.totalCount = Math.max(0, state.totalCount - 1);
                // Clear selected region settings if it was the deleted one
                if (state.selectedRegionSettings?.id === action.payload?.id) {
                    state.selectedRegionSettings = null;
                }
            })
            .addCase(deleteRegionSettings.rejected, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.error = action.payload || "Failed to delete region settings";
            });

        // Get Region Settings by ID
        builder
            .addCase(getRegionSettingsById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getRegionSettingsById.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.selectedRegionSettings = action.payload;
            })
            .addCase(getRegionSettingsById.rejected, (state, action: PayloadAction<any>) => {
                state.loading = false;
                state.error = action.payload || "Failed to fetch region settings";
            });
    },
});

export const {
    clearSelectedRegionSettings,
    clearError,
    setCurrentPage,
    setCurrentLimit,
} = regionSettingsSlice.actions;

export default regionSettingsSlice.reducer;
